import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { CircleHelp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/AuthContext';
import { userHasModuleAccess } from '@/lib/moduleAccess';
import { authApi } from '@/api/apiClient';
import { toast } from 'sonner';
import '@/components/guided-tour/guided-tour.css';

const GuidedTourCtx = createContext(null);

function waitForSelector(selector, { timeoutMs = 4000, intervalMs = 100 } = {}) {
  return new Promise((resolve) => {
    const t0 = Date.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) {
        resolve(el);
        return;
      }
      if (Date.now() - t0 >= timeoutMs) {
        resolve(null);
        return;
      }
      window.setTimeout(tick, intervalMs);
    };
    tick();
  });
}

function mapStepsForDriver(steps) {
  return (steps || []).map((s) => ({
    element: s.selector,
    popover: {
      title: s.title,
      description: s.body,
      side: 'bottom',
      align: 'start',
    },
  }));
}

/**
 * Resolve passos com elementos no DOM e inicia o Driver.
 * @param {{ onTourDestroyed?: () => void }} opts — `onTourDestroyed` após fechar ou concluir (tour que chegou a abrir).
 */
async function launchGuidedTour(steps, opts = {}) {
  const onTourDestroyed = opts.onTourDestroyed;
  if (!steps?.length) {
    toast.message('Não há passos de tour para esta página.');
    return false;
  }
  const resolved = [];
  for (const s of steps) {
    const el = await waitForSelector(s.selector);
    if (el) {
      resolved.push(s);
    }
  }
  if (resolved.length === 0) {
    toast.error('Não foi possível localizar os elementos do tour nesta vista.');
    return false;
  }
  const driverSteps = mapStepsForDriver(resolved);
  const d = driver({
    showProgress: true,
    progressText: '{{current}} de {{total}}',
    nextBtnText: 'Próximo',
    prevBtnText: 'Anterior',
    doneBtnText: 'Concluir',
    showButtons: ['next', 'previous', 'close'],
    popoverClass: 'auction-guided-tour-popover',
    steps: driverSteps,
    onDestroyed: () => {
      onTourDestroyed?.();
    },
  });
  d.drive();
  return true;
}

export function GuidedTourProvider({ children, currentPageName }) {
  const { user, empresa, isLoadingAuth, checkAppState } = useAuth();

  const { data: tourPayload, isLoading } = useQuery({
    queryKey: ['guided-tour-config'],
    queryFn: () => authApi.guidedTour(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: !!user && !isLoadingAuth,
  });

  const moduleId = currentPageName || 'Dashboard';

  const canAccessModule = useMemo(() => {
    if (!user) return false;
    if (moduleId === 'Empresas' && user.role !== 'admin' && user.perfil !== 'admin') {
      return false;
    }
    return userHasModuleAccess(user, empresa, moduleId);
  }, [user, empresa, moduleId]);

  const stepsForModule = useMemo(() => {
    const mod = tourPayload?.modules?.[moduleId];
    if (!mod?.steps?.length) return [];
    return mod.steps;
  }, [tourPayload, moduleId]);

  const showTourButton = canAccessModule && stepsForModule.length > 0 && !isLoading;

  /** Intro automático global: uma vez por conta (servidor), independente do browser. */
  const introCompleto = !!user?.guided_tour_intro_completo;

  const stepsRef = useRef(stepsForModule);
  stepsRef.current = stepsForModule;

  const persistIntroCompleto = useCallback(async () => {
    try {
      await authApi.ackGuidedTourIntro();
      await checkAppState();
    } catch (e) {
      console.warn('[guided-tour] ack-intro', e);
    }
  }, [checkAppState]);

  const startTour = useCallback(async () => {
    if (autoTimerRef.current) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
    await launchGuidedTour(stepsRef.current, { onTourDestroyed: persistIntroCompleto });
  }, [persistIntroCompleto]);

  const autoTimerRef = useRef(null);
  const stepsKey = useMemo(() => JSON.stringify(stepsForModule.map((s) => s.selector)), [stepsForModule]);

  useEffect(() => {
    if (!showTourButton || introCompleto) {
      return undefined;
    }
    if (autoTimerRef.current) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
    autoTimerRef.current = window.setTimeout(() => {
      autoTimerRef.current = null;
      void launchGuidedTour(stepsRef.current, { onTourDestroyed: persistIntroCompleto });
    }, 700);
    return () => {
      if (autoTimerRef.current) {
        clearTimeout(autoTimerRef.current);
        autoTimerRef.current = null;
      }
    };
  }, [moduleId, showTourButton, stepsKey, introCompleto, persistIntroCompleto]);

  const value = useMemo(
    () => ({ startTour, showTourButton, moduleId }),
    [startTour, showTourButton, moduleId]
  );

  return <GuidedTourCtx.Provider value={value}>{children}</GuidedTourCtx.Provider>;
}

export function useGuidedTour() {
  const v = useContext(GuidedTourCtx);
  if (!v) {
    return { startTour: () => {}, showTourButton: false };
  }
  return v;
}

export function GuidedTourHeaderButton() {
  const { startTour, showTourButton } = useGuidedTour();
  if (!showTourButton) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="gap-1.5 text-slate-600 hover:text-blue-700"
      onClick={() => startTour()}
    >
      <CircleHelp className="h-4 w-4 shrink-0" />
      <span className="hidden sm:inline">Tour guiado</span>
    </Button>
  );
}
